import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BaseHttpClientService } from './base-http-client.service';

export interface WorkOrderListFilters {
  asset?: string;
  states?: string[];
}

@Injectable()
export class SiteWorkOrdersService extends BaseHttpClientService {

  constructor(private http: HttpClient) {
    super()
  }

  // close work order
  public closeWorkOrder(workOrderId) {
    const data = {
      workOrderId: workOrderId,
    };

    return this.http
      .post(environment.apiUrl + 'api/ext-work-order/close-work-order', data)
      .pipe(map(this.handleDataWithHeaders))
      .pipe(catchError(this.handleError));
  }

  public overrideSurrenderedWorkOrder(workOrderId) {
    return this.http
      .post(environment.apiUrl + 'api/ext-work-order/reverse-surrender/' + workOrderId, {})
      .pipe(map(this.handleDataWithHeaders))
      .pipe(catchError(this.handleError));
  }

  // Get all work orders, as the slim list rows the work order lists render (PLATFORM-49).
  // getWorkOrderById still returns the full work order.
  public getAllWorkOrders(pagination, sortArray: string[], activeOnly: boolean = false, forDelegate: boolean = false, actionRequiredFilter: boolean = false, filters: WorkOrderListFilters = {}): Observable<any> {
    let params = new HttpParams()
      .set('size', pagination.size)
      .set('page', pagination.page);
    sortArray.forEach((sort) => {
      params = params.append('sort', sort);
    });

    if (activeOnly) {
      params = params.set('active', 'true');
    }

    if (forDelegate) {
      params = params.set('forDelegate', 'true');
    }

    if (actionRequiredFilter) {
      params = params.set('actionRequired', 'true');
    }

    params = this.appendWorkOrderListFilters(params, filters);

    const headers = this.applyLatestOnlyHeader();

    return this.http
      .get(environment.apiUrl + 'api/ext-work-order/list', { observe: 'response', params, headers })
      .pipe(map(this.handleDataWithHeaders))
      .pipe(map((response) => this.normalizeWorkOrderResponse(response)))
      .pipe(catchError(this.handleError));
  }

  public getWorkOrdersForDash(): Observable<any> {
    return this.http
      .get(environment.apiUrl + 'api/ext-work-order/for-dashboard', { observe: 'response' })
      .pipe(map(this.handleDataWithHeaders))
      .pipe(catchError(this.handleError));
  }

  // Get individual work order by id
  public getWorkOrderById(workorderid): Observable<any>{
    return this.http
      .get(environment.apiUrl + 'api/ext-work-order/' + workorderid)
      .pipe(map((workOrder) => this.normalizeWorkOrder(workOrder)))
      .pipe(catchError(this.handleError));
  }

  // Get all work orders by site id, as slim list rows (PLATFORM-49)
  public getAllWorkOrdersBySite(siteid, pagination, sortArray: string[], actionRequiredFilter: boolean = false, filters: WorkOrderListFilters = {}) {
    let params = new HttpParams()
    .set('size', pagination.size)
    .set('page', pagination.page)
    .set('portal', 'true');

    sortArray.forEach((sort) => {
      params = params.append('sort', sort);
    });

    if (actionRequiredFilter) {
      params = params.append('actionRequired', 'true');
    }

    params = this.appendWorkOrderListFilters(params, filters);

    const headers = this.applyLatestOnlyHeader();

    return this.http
      .get(environment.apiUrl + 'api/ext-work-order/by-site/' + siteid + '/list', { observe: 'response', params, headers })
      .pipe(map(this.handleDataWithHeaders))
      .pipe(map((response) => this.normalizeWorkOrderResponse(response)))
      .pipe(catchError(this.handleError));
  }

  public getAllApprovedWorkOrdersByAsset(assetId) {
    return this.http
      .get(environment.apiUrl + 'api/ext-work-order/approved-by-asset/' + assetId + '?size=10000&page=0')
      .pipe(map((workOrders: any[]) => this.normalizeWorkOrders(workOrders)))
      .pipe(catchError(this.handleError));
  }

  public getAllOpenWorkOrdersBySiteForUser(siteId, skylarkUserId) {
    return this.http
      .get(environment.apiUrl + 'api/ext-work-order/open-by-site/' + siteId + '/skylark-user/' + skylarkUserId + '?size=10000&page=0')
      .pipe(map((workOrders: any[]) => this.normalizeWorkOrders(workOrders)))
      .pipe(catchError(this.handleError));
  }

  public getAllContactsForWorkOrder(workOrderId: number) {
    return this.http
      .get(environment.apiUrl + 'api/ext-work-order/' + workOrderId + '/contacts')
      .pipe(catchError(this.handleError));
  }

  public getActionRequiredCount(): Observable<number> {
    return this.http
      .get(environment.apiUrl + 'api/ext-work-order/count-action-required')
      .pipe(map((response: number) => response))
      .pipe(catchError(this.handleError));
  }

  public getActionRequiredCountForSite(siteId: string): Observable<number> {
    return this.http
      .get(environment.apiUrl + 'api/ext-work-order/count-action-required/for-site/' + siteId)
      .pipe(map((response: number) => response))
      .pipe(catchError(this.handleError));
  }

  // Create Work Order
  public createWorkOrder(obj): Observable<any> {
    const jsonDataMultipartName = 'workOrder';
    const fileMultipartName = 'rams';

    const body = {
      assetId: obj.assetId,
      description: obj.description,
      minimumPartySize: obj.minimumPartySize,
      scheduledStartTime: obj.scheduledStartTime,
      scheduledEndTime: obj.scheduledEndTime,
      exemptFromAssetScheduling: obj.exemptFromAssetScheduling,
      checkOnSiteFormId: obj.checkOnSiteFormId,
      checkOffSiteFormId: obj.checkOffSiteFormId,
    };

    // multipart/form-data, append each part
    const formData = new FormData();
    formData.append(jsonDataMultipartName, new Blob([JSON.stringify(body)], { type: 'application/json' }));

    obj.ramsArray.forEach((file: File) => {
      formData.append(fileMultipartName, file, file.name);
    });

    return this.http
      .post(environment.apiUrl + 'api/ext-work-order/create', formData)
      .pipe(
        catchError((error) => {
          console.log('Error', error.error);
          if (error.error && error.error.status === 400 && error.error.errorKey === 'Infected') {
            throw new Error('The file is infected and cannot be uploaded!');
          }
          return this.handleError(error);
        })
      );
  }

  // Add Additional RAMS to Work Order
  public addRamsToWorkOrder(obj): Observable<any> {
    const jsonDataMultipartName = 'workOrder';
    const fileMultipartName = 'rams';

    const body = {
      workOrderId: obj.id,
    };

    // multipart/form-data, append each part
    const formData = new FormData();
    formData.append(jsonDataMultipartName, new Blob([JSON.stringify(body)], { type: 'application/json' }));

    obj.ramsArray.forEach((file: File) => {
      formData.append(fileMultipartName, file, file.name);
    });

    return this.http
      .post(environment.apiUrl + 'api/ext-work-order/add-rams', formData)
      .pipe(
        catchError((error) => {
          console.log('Error', error.error);
          if (error.error && error.error.status === 400 && error.error.errorKey === 'Infected') {
            throw new Error('The file is infected and cannot be uploaded!');
          }
          return this.handleError(error);
        })
      );
  }

  // Delete RAMS
  public deleteRams(id) {
    return this.http
      .delete(environment.apiUrl + 'api/ext-file/' + id)
      .pipe(catchError(this.handleError));
  }

  // Update Work Order
  public updateWorkOrder(obj) {
    return this.http
      .put(environment.apiUrl + 'api/ext-work-order', obj)
      .pipe(catchError(this.handleError));
  }

  // Add comment to Work Order
  addWorkOrderComment(obj) {
    return this.http
      .put(environment.apiUrl + 'api/ext-work-order/add-comment', obj)
      .pipe(catchError(this.handleError));
  }

  // Delete a work order
  public deleteWorkOrder(workorderid) {
    return this.http
      .delete(environment.apiUrl + 'api/ext-work-order/' + workorderid)
      .pipe(catchError(this.handleError));
  }

  public updateTocStatus(obj, workOrderId) {
    return this.http
      .patch(environment.apiUrl +  `api/ext-work-order/${workOrderId}/toc-status`, obj)
      .pipe(catchError(this.handleError));
  }

  public createWorkOrderFromProcedure(procedureId: any, assetId: any, createValues: any): Observable<any> {
    return this.http
      .post(environment.apiUrl + 'api/ext-work-order/create-from-procedure/' + procedureId + '/on-asset/' + assetId, createValues)
      .pipe(map(this.handleDataWithHeaders))
      .pipe(map((response) => this.normalizeWorkOrderResponse(response)))
      .pipe(catchError(this.handleError));
  }

  public getUserDetails(skylarkUserLoginName) {
    return this.http
      .get(environment.apiUrl + 'api/ext-skylark-user/by-login/' + skylarkUserLoginName)
      .pipe(catchError(this.handleError));
  }

  public handleDataWithHeaders(res: HttpResponse<any>) {
    const headers = res.headers;
    const body = res.body;
    const data = {
      headers: headers,
      body: body,
    };
    return data;
  }

  // a partial asset name and the Status quick-picks (backend WorkOrderState names)
  private appendWorkOrderListFilters(params: HttpParams, filters: WorkOrderListFilters): HttpParams {
    const asset = (filters.asset || '').trim();
    if (asset) {
      params = params.set('asset', asset);
    }

    (filters.states || []).forEach((state) => {
      params = params.append('state', state);
    });

    return params;
  }

  private normalizeWorkOrderResponse(response: any): any {
    if (!response) {
      return response;
    }

    if (Array.isArray(response.body)) {
      response.body = this.normalizeWorkOrders(response.body);
      return response;
    }

    response.body = this.normalizeWorkOrder(response.body);
    return response;
  }

  private normalizeWorkOrders(workOrders: any[]): any[] {
    if (!Array.isArray(workOrders)) {
      return workOrders;
    }

    return workOrders.map((workOrder) => this.normalizeWorkOrder(workOrder));
  }

  private normalizeWorkOrder(workOrder: any): any {
    if (!workOrder || !workOrder.ownerWorkerUser) {
      return workOrder;
    }

    const ownerWorkerUser = workOrder.ownerWorkerUser;
    const maintenanceName = (ownerWorkerUser.maintenance && ownerWorkerUser.maintenance.name) || ownerWorkerUser.maintenanceName;

    if (maintenanceName) {
      ownerWorkerUser.maintenance = {
        ...ownerWorkerUser.maintenance,
        name: maintenanceName,
      };
    }

    return workOrder;
  }

  public showFile(folder, fileName): void {
    const uri = folder + '/' + fileName;
    this.http.get(uri, { responseType: 'blob' }).subscribe((response) => {
      const fileURL = URL.createObjectURL(response);
      if (response.type.startsWith('image') || response.type.endsWith('pdf')) {
        window.open(fileURL, '_blank');
      } else {
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.setAttribute('style', 'display: none');
        a.href = fileURL;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(fileURL);
      }
    });
  }
}
